import { useEffect, useState } from 'react';

import { useGlobalLightbox, LightboxId } from '../../../hooks';

/**
 * A custom React hook that listens for a specific keyboard shortcut (Ctrl + Alt + S)
 * to open a global lightbox. It ensures that the lightbox is not opened multiple times
 * if the user holds down the hotkey.
 *
 * @returns - An object containing:
 *   - `hasBeenOpened` (boolean): Indicates whether the lightbox has been opened at least once.
 *
 * @example
 * ```ts
 *  const { hasBeenOpened } = useHotKeys();
 * ```
 */
export function useHotKeys() {
  const { open, isOpen } = useGlobalLightbox({
    id: LightboxId.TosaDevTools,
  });
  const [isOpening, setIsOpening] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  useEffect(() => {
    window.document.addEventListener('keydown', e => {
      const keyName = e.key;

      if (e.isComposing || keyName === 'Control') {
        // do not alert when only Control key is pressed.
        return;
      }

      if (e.ctrlKey && e.altKey && e.code === 'KeyS' && !isOpening) {
        e.preventDefault();
        setIsOpening(true);
      }
    });
  }, [isOpening]);

  useEffect(() => {
    // This hook ensures that if a user holds down the hotkey we don't call open
    // multiple times resulting in the modal rapidly opening and closing
    const openModal = async () => {
      // TODO: This type needs to fixed in CORGI
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await open();
      setIsOpening(false);
      setHasBeenOpened(true);
    };

    if (isOpening && !isOpen) {
      void openModal();
    }
  }, [isOpen, open, isOpening]);

  return { hasBeenOpened };
}
